import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useMytaverseEvent } from '@core/dashboard/providers';
import { useWorldOnScreen } from '@core/dashboard/hooks/useWorldOnScreen';

import { BRANDING_TRANSITION_MS } from './constants';

interface ISeen {
  roomId: string | null;
  brandingId: string | null;
}

export interface IBrandingTransition {
  transitionId: number | null;
  isReady: boolean;
  dismiss: () => void;
}

/**
 * Raises a fresh transition id every time the world changes configuration, marks it ready after
 * `BRANDING_TRANSITION_MS`, and drops it back to null when the user dismisses the cover.
 *
 * The hold ends the *waiting*, not the cover: ten seconds is a guess at how long UE needs, not a
 * signal that it is finished, so taking the screen away on that guess could uncover a map still
 * popping its spots in. The user takes it down instead, on a map they can see is ready.
 *
 * Derived from the *effective* branding — `currentBrandingId ?? currentRoom.activeBrandingId`, the
 * same resolution `useActiveBrandingId` makes — rather than announced by the switchers, so every
 * path that makes a configuration live is covered by construction: the editor's quick switcher and
 * Admin Tools both go through `useBrandingSwitch`, creating a configuration activates it from a
 * third place, and a remote activation arrives as a room update with no call site at all.
 *
 * Two changes are deliberately NOT a switch: the first resolve of the session (nothing was on
 * screen to change) and a room change (the configuration differs because the room does, and the
 * teleport has its own treatment).
 *
 * Nothing at all happens while a loader owns the screen — the entry cinema or a reconnect. There is
 * no half-dressed map to hide when the map is not on screen, and the loader that IS up says
 * something truer about what the page is doing. A configuration that lands during a loader becomes
 * the new baseline rather than a pending switch, so it does not pop a cover over a world the user is
 * seeing for the first time.
 *
 * @returns `transitionId` (null while nothing is covered), `isReady` — the hold is over and the
 * screen is waiting to be dismissed — and `dismiss`, which takes the cover down.
 */
export const useBrandingTransition = (): IBrandingTransition => {
  const { currentBrandingId, currentRoom } = useMytaverseEvent() || {};
  const worldOnScreen = useWorldOnScreen();

  const roomId = currentRoom?.roomId ?? null;
  const brandingId = currentBrandingId ?? currentRoom?.activeBrandingId ?? null;

  const [transitionId, setTransitionId] = useState<number | null>(null);
  const [isReady, setIsReady] = useState(false);
  const seenRef = useRef<ISeen | null>(null);
  const nextIdRef = useRef(0);

  useEffect(() => {
    const seen = seenRef.current;
    seenRef.current = { roomId, brandingId };

    // Also the way a cover already up is taken down when the picture goes away mid-hold: a reclaim
    // or a dead node hands the screen to the loader, and the re-skin stopped being the story. A
    // cover waiting to be dismissed goes the same way — there is no map behind it to hand back.
    if (!worldOnScreen) {
      setTransitionId(null);
      setIsReady(false);
      return;
    }

    if (
      !brandingId ||
      !seen ||
      !seen.brandingId ||
      seen.roomId !== roomId ||
      seen.brandingId === brandingId
    ) {
      return;
    }

    nextIdRef.current += 1;
    setTransitionId(nextIdRef.current);
    setIsReady(false);
  }, [roomId, brandingId, worldOnScreen]);

  // Keyed on the id rather than on a boolean: a second switch inside the window has to restart the
  // ten seconds, and `setActive(true)` on an already-true flag would not re-run this. A switch that
  // lands on an already-dismissable cover restarts it too — the previous configuration is no longer
  // the one being applied, so its "Continue" would hand back a map that is changing again.
  useEffect(() => {
    if (transitionId === null) {
      return undefined;
    }

    const timer = setTimeout(() => setIsReady(true), BRANDING_TRANSITION_MS);

    return () => clearTimeout(timer);
  }, [transitionId]);

  const dismiss = useCallback(() => {
    setTransitionId(null);
    setIsReady(false);
  }, []);

  return useMemo(
    () => ({ transitionId, isReady, dismiss }),
    [transitionId, isReady, dismiss],
  );
};
